/**
 * Centralized lone pair positioning logic
 *
 * This class handles all lone pair positioning calculations, including:
 * - Selecting the best LP direction away from destination
 * - Avoiding atom overlap with directional adjustments
 * - Calculating arrow start points from LP positions
 *
 * All magic numbers and complex conditionals from the original implementation
 * are isolated here for easy modification.
 */

import { v3 } from "@alchemiesolns/maths";
import { findClosestOppositeVector } from "../core/geometry";
import {
  LP_CONSTANTS,
  DIRECTION_VECTORS,
  isDownLeftDiagonal,
  isUpLeftDiagonal,
  isRightDown,
  isLeftUp,
  isUpRight,
  isDownLeft,
} from "../core/constants";
import type { Point } from "../types";

/**
 * Configuration options for lone pair positioning
 * Allows overriding default behavior for different contexts
 */
export interface LonePairPositioningConfig {
  /**
   * Additional offset to add to base LP offset for start point calculation
   * Default: 0.8 (from original helper.ts:36)
   */
  startPointOffsetAddition?: number;

  /**
   * Threshold for direction component checking
   * Default: -0.9 (from original EndPointMath.ts:115)
   */
  directionThreshold?: number;

  /**
   * Horizontal distance threshold for special case handling
   * Default: -10 (from original EndPointMath.ts:120)
   */
  horizontalDistanceThreshold?: number;

  /**
   * If true, skip overlap avoidance adjustments
   * Default: false
   */
  skipOverlapAvoidance?: boolean;

  /**
   * Strategy for handling LP direction selection when overlap would occur
   * - 'strict': Only use actual LP directions (may cause overlap)
   * - 'hybrid': Prefer LP directions but allow deflection to avoid overlap
   * - 'flexible': Allow any direction that avoids overlap
   * Default: 'hybrid'
   */
  overlapStrategy?: 'strict' | 'hybrid' | 'flexible';

  /**
   * Distance threshold for detecting if arrow passes through atom
   * Default: 1.2 (slightly larger than typical atom radius)
   */
  overlapThreshold?: number;
}

/**
 * Manages lone pair positioning for arrow calculations
 */
export class LonePairPositioner {
  private config: Required<LonePairPositioningConfig>;

  constructor(config: LonePairPositioningConfig = {}) {
    this.config = {
      startPointOffsetAddition:
        config.startPointOffsetAddition ?? LP_CONSTANTS.START_POINT_OFFSET_ADDITION,
      directionThreshold:
        config.directionThreshold ?? LP_CONSTANTS.DIRECTION_THRESHOLD,
      horizontalDistanceThreshold:
        config.horizontalDistanceThreshold ??
        LP_CONSTANTS.HORIZONTAL_DISTANCE_THRESHOLD,
      skipOverlapAvoidance: config.skipOverlapAvoidance ?? false,
      overlapStrategy: config.overlapStrategy ?? 'hybrid',
      overlapThreshold: config.overlapThreshold ?? 1.2,
    };
  }

  /**
   * Select the best lone pair direction away from the destination
   * and adjust it to avoid atom overlap
   *
   * @param lpPoint - Lone pair point with available directions
   * @param destPos - Destination position to point away from
   * @returns Selected and adjusted direction vector
   */
  public selectAndAdjustDirection(lpPoint: Point, destPos: number[]): number[] {
    // Step 1: Find the LP direction most opposite to the destination
    let direction = findClosestOppositeVector(
      lpPoint.direction,
      lpPoint.position,
      destPos
    );

    // Step 2: Apply overlap avoidance based on strategy
    if (!this.config.skipOverlapAvoidance) {
      // For 'strict' strategy, only use the traditional avoidAtomOverlap logic
      if (this.config.overlapStrategy === 'strict') {
        direction = this.avoidAtomOverlap(direction, lpPoint.position, destPos, lpPoint.direction);
      } else {
        // For 'hybrid' or 'flexible' strategies, check for overlap and use fallbacks
        const testStart = this.calculateStartPoint(lpPoint, direction);

        // Check if the selected direction would cause overlap
        if (this.wouldOverlapAtom(testStart, destPos, lpPoint.position)) {
          // Try the traditional avoidAtomOverlap adjustment first
          const adjusted = this.avoidAtomOverlap(direction, lpPoint.position, destPos, lpPoint.direction);
          const adjustedStart = this.calculateStartPoint(lpPoint, adjusted);

          // If still overlapping, use perpendicular deflection (hybrid/flexible only)
          if (this.wouldOverlapAtom(adjustedStart, destPos, lpPoint.position)) {
            direction = this.calculatePerpendicularDeflection(
              direction,
              lpPoint.position,
              destPos,
              lpPoint
            );
          } else {
            direction = adjusted;
          }
        }
        // else: no overlap, use original direction
      }
    }

    return direction;
  }

  /**
   * Calculate the arrow start point from a lone pair position
   *
   * @param lpPoint - Lone pair point
   * @param direction - Selected direction vector
   * @returns Arrow start position
   */
  public calculateStartPoint(lpPoint: Point, direction: number[]): number[] {
    const scalar = lpPoint.offset + this.config.startPointOffsetAddition;
    return v3.addScaledVector(lpPoint.position, direction, scalar);
  }

  /**
   * Calculate start direction from lone pair to destination
   * (normalized direction vector for curve calculation)
   *
   * @param lpPoint - Lone pair point
   * @param destPos - Destination position
   * @returns Normalized direction vector
   */
  public calculateStartDirection(lpPoint: Point, destPos: number[]): number[] {
    const direction = this.selectAndAdjustDirection(lpPoint, destPos);
    const lpPos = this.calculateStartPoint(lpPoint, direction);
    return v3.normalize(v3.subVectors(lpPos, lpPoint.position));
  }

  /**
   * Find the closest LP direction to a target direction
   *
   * @param targetDir - Desired direction (may not be an actual LP direction)
   * @param availableLPDirections - All available LP directions
   * @returns The closest available LP direction
   */
  private findClosestLPDirection(
    targetDir: number[],
    availableLPDirections: number[][]
  ): number[] {
    if (availableLPDirections.length === 0) {
      return targetDir; // Fallback, shouldn't happen
    }

    let bestDir = availableLPDirections[0];
    let bestDot = v3.dotVectors(v3.normalize(targetDir), v3.normalize(bestDir));

    for (let i = 1; i < availableLPDirections.length; i++) {
      const lpDir = availableLPDirections[i];
      const dot = v3.dotVectors(v3.normalize(targetDir), v3.normalize(lpDir));
      if (dot > bestDot) {
        bestDot = dot;
        bestDir = lpDir;
      }
    }

    return bestDir;
  }

  /**
   * Check if an arrow from startPos to destPos would pass through the atom
   *
   * Uses closest point on line segment calculation to determine if the arrow
   * path comes too close to the source atom center.
   *
   * @param startPos - Arrow start position
   * @param destPos - Arrow destination position
   * @param atomPos - Atom center position
   * @returns True if arrow would overlap atom
   */
  private wouldOverlapAtom(
    startPos: number[],
    destPos: number[],
    atomPos: number[]
  ): boolean {
    // Calculate vector from start to destination
    const arrowVector = v3.subVectors(destPos, startPos);
    const arrowLengthSq = v3.dotVectors(arrowVector, arrowVector);

    // Edge case: arrow has zero length
    if (arrowLengthSq < 0.0001) {
      return v3.distance(startPos, atomPos) < this.config.overlapThreshold;
    }

    // Calculate parameter t for closest point on line segment
    // t = 0 at startPos, t = 1 at destPos
    const atomToStart = v3.subVectors(atomPos, startPos);
    const t = v3.dotVectors(atomToStart, arrowVector) / arrowLengthSq;

    // Clamp t to [0, 1] to stay within line segment
    const tClamped = Math.max(0, Math.min(1, t));

    // Calculate closest point on segment
    const closestPoint = v3.addVectors(
      startPos,
      v3.scale(arrowVector, tClamped)
    );

    // Check if closest point is within threshold of atom
    const distance = v3.distance(closestPoint, atomPos);
    return distance < this.config.overlapThreshold;
  }

  /**
   * Calculate a perpendicular deflection direction to avoid atom overlap
   *
   * When all available LP directions would cause the arrow to pass through
   * the atom, this method calculates a perpendicular deflection that:
   * 1. Points away from the atom-destination axis
   * 2. Blends with the closest LP direction for chemical validity
   * 3. Provides the best clearance from the atom
   *
   * @param originalDir - Original selected LP direction
   * @param atomPos - Atom center position
   * @param destPos - Destination position
   * @param lpPoint - Lone pair point with available directions
   * @returns Deflected direction that avoids overlap
   */
  private calculatePerpendicularDeflection(
    originalDir: number[],
    atomPos: number[],
    destPos: number[],
    lpPoint: Point
  ): number[] {
    // Vector from atom to destination (normalized)
    const toDestDir = v3.normalize(v3.subVectors(destPos, atomPos));

    // Calculate both perpendicular directions (90° on each side)
    const perp1 = v3.normalize([-toDestDir[1], toDestDir[0], 0]);
    const perp2 = v3.normalize([toDestDir[1], -toDestDir[0], 0]);

    // Find closest LP directions to each perpendicular
    const closest1 = this.findClosestLPDirection(perp1, lpPoint.direction);
    const closest2 = this.findClosestLPDirection(perp2, lpPoint.direction);

    // Blend perpendicular with closest LP (30% perp, 70% LP for chemical validity)
    const blend1 = v3.normalize(
      v3.addVectors(v3.scale(perp1, 0.3), v3.scale(closest1, 0.7))
    );
    const blend2 = v3.normalize(
      v3.addVectors(v3.scale(perp2, 0.3), v3.scale(closest2, 0.7))
    );

    // Test both blended directions for overlap
    const start1 = this.calculateStartPoint(lpPoint, blend1);
    const start2 = this.calculateStartPoint(lpPoint, blend2);

    const overlaps1 = this.wouldOverlapAtom(start1, destPos, atomPos);
    const overlaps2 = this.wouldOverlapAtom(start2, destPos, atomPos);

    // If one is clear and the other isn't, use the clear one
    if (!overlaps1 && overlaps2) return blend1;
    if (!overlaps2 && overlaps1) return blend2;

    // If both clear or both overlap, choose based on visual aesthetics
    // Prefer the one that deviates less from original direction
    const dot1 = Math.abs(v3.dotVectors(blend1, originalDir));
    const dot2 = Math.abs(v3.dotVectors(blend2, originalDir));

    return dot1 > dot2 ? blend1 : blend2;
  }

  /**
   * CORE COMPLEXITY: Avoid atom overlap by adjusting direction
   *
   * This contains all the magic number logic from EndPointMath.ts:113-182.
   * Isolated here so modifications to LP positioning are straightforward.
   *
   * The logic checks the relationship between LP direction and the vector
   * from atom to destination, then applies hardcoded directional overrides
   * to prevent arrows from overlapping the atom.
   *
   * IMPORTANT: This now only returns directions from availableLPDirections to ensure
   * arrow start points correspond to actual lone pair positions, not arbitrary bond angles.
   *
   * @param direction - Initial selected direction
   * @param atomPos - Atom position
   * @param destPos - Destination position
   * @param availableLPDirections - All available LP directions to choose from
   * @returns Adjusted direction to avoid overlap (guaranteed to be from availableLPDirections)
   */
  private avoidAtomOverlap(
    direction: number[],
    atomPos: number[],
    destPos: number[],
    availableLPDirections: number[][]
  ): number[] {
    const sub = v3.normalize(v3.subVectors(atomPos, destPos));

    // Case 1: Direction is diagonal down-left
    if (isDownLeftDiagonal(direction)) {
      if (sub[0] < this.config.horizontalDistanceThreshold) {
        if (sub[1] > 0) {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.RIGHT_UP], availableLPDirections);
        }
      } else if (sub[0] < 0) {
        return this.findClosestLPDirection([...DIRECTION_VECTORS.LEFT_UP], availableLPDirections);
      }
    }

    // Case 2: Direction is diagonal up-left
    if (isUpLeftDiagonal(direction)) {
      if (sub[0] < this.config.horizontalDistanceThreshold) {
        if (sub[1] > 0) {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.RIGHT_UP], availableLPDirections);
        }
      } else if (sub[0] < 0) {
        return this.findClosestLPDirection([...DIRECTION_VECTORS.LEFT_UP], availableLPDirections);
      }
    }

    // Case 3: Direction is right-down
    if (isRightDown(direction)) {
      if (sub[0] > 0) {
        if (sub[1] > 0) {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.DOWN], availableLPDirections);
        } else {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.UP], availableLPDirections);
        }
      }
    }

    // Case 4: Direction is left-up
    if (isLeftUp(direction)) {
      if (sub[0] < 0) {
        if (sub[1] > 0) {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.DOWN], availableLPDirections);
        } else {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.UP], availableLPDirections);
        }
      }
    }

    // Case 5: Direction is up-right
    if (isUpRight(direction)) {
      if (sub[1] > 0) {
        if (sub[0] > 0) {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.LEFT], availableLPDirections);
        } else {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.RIGHT], availableLPDirections);
        }
      }
    }

    // Case 6: Direction is down-left
    if (isDownLeft(direction)) {
      if (sub[1] < 0) {
        if (sub[0] > 0) {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.LEFT], availableLPDirections);
        } else {
          return this.findClosestLPDirection([...DIRECTION_VECTORS.RIGHT], availableLPDirections);
        }
      }
    }

    // No adjustment needed
    return direction;
  }

  /**
   * Get the current configuration (useful for debugging)
   */
  public getConfig(): Readonly<Required<LonePairPositioningConfig>> {
    return { ...this.config };
  }

  /**
   * Update configuration (useful for testing different positioning strategies)
   */
  public updateConfig(newConfig: Partial<LonePairPositioningConfig>): void {
    this.config = {
      ...this.config,
      ...newConfig,
    };
  }
}

/**
 * Default singleton instance for backwards compatibility
 */
export const defaultLonePairPositioner = new LonePairPositioner();
